import { Controller } from "@hotwired/stimulus";
import { sendJSON } from "../api.js";

// P24 return page: poll GET statusUrl every `interval` ms until paid/failed or `timeout` from the latest start/retry.
// retry() hides the timeout panel and restarts the clock; disconnect() clears timers and aborts the in-flight fetch.
//
// p24fast: besides polling, JS drives the P24 verification itself — POST reconcileUrl at 15 s,
// then every 30 s (45/75/105/135 s): at most 5 POSTs ever (also across retry()). Stops on a terminal
// status, on {"queued": false, "reason": "exhausted"} or on disconnect; POST errors are ignored.
// The loader bar fills over `timeout` ms (--ls-progress) and the stage line follows the clock.
//
// provider_state values (seq 8/9):
//   "not_paid"        → no payment yet at P24; keep waiting and polling.
//   "paid_unconfirmed"→ webhook imminent; keep polling.
//   "unknown" | null  → current behaviour (keep polling).
//
// refund_pending (seq 16):
//   true + status === "paid" → late or unfulfilled payment; show refundPending panel,
//                              never show successOrder (no leads delivered).
const PROGRESS_TICK_MS = 1000;
// Verification POST schedule from connect(): 15 s, then every 30 s up to 135 s — 5 attempts, never more.
const RECONCILE_SLOTS_MS = [15000, 45000, 75000, 105000, 135000];
const STAGE_SLOW_AT_MS = 90000;
const STAGE_WAITING = "Czekamy na potwierdzenie z Przelewy24…";
const STAGE_CHECKING = "Sprawdzamy płatność bezpośrednio w Przelewy24…";
const STAGE_SLOW = "To trwa dłużej niż zwykle — nie zamykaj tej strony.";

export default class extends Controller {
  static values = {
    statusUrl: String,
    interval:  { type: Number, default: 2000 },
    timeout:   { type: Number, default: 150000 },
    reconcileUrl: String,
  };

  static targets = [
    "pending",
    "fill",
    "stage",
    "successOrder",
    "successTopUp",
    "failed",
    "notCompletedOrder",
    "notCompletedTopUp",
    "timeout",
    "refundPending",
    "orderNumber",
    "amount",
  ];

  connect() {
    this.#startPolling();
  }

  disconnect() {
    this.#stopPolling();
  }

  retry() {
    this.#stopPolling();
    this.#showPanel("pending");
    this.#startPolling();
  }

  // --- private ---

  #timer = null;
  #startedAt = 0;
  #abortController = null;
  #progressTimer = null;
  #reconcileTimer = null;
  #reconcileSent = 0;
  #reconcileDone = false;

  #startPolling() {
    this.#startedAt = Date.now();
    this.#abortController = new AbortController();
    this.#renderProgress();
    this.#progressTimer = setInterval(() => this.#renderProgress(), PROGRESS_TICK_MS);
    this.#scheduleReconcile();
    this.#poll();
    this.#timer = setInterval(() => this.#poll(), this.intervalValue);
  }

  #renderProgress() {
    const elapsed = Date.now() - this.#startedAt;
    const pct = Math.min(100, Math.max(0, (elapsed / this.timeoutValue) * 100));
    if (this.hasFillTarget) this.fillTarget.style.setProperty("--ls-progress", `${pct.toFixed(1)}%`);
    if (this.hasStageTarget) {
      let text = STAGE_WAITING;
      if (elapsed >= STAGE_SLOW_AT_MS) text = STAGE_SLOW;
      else if (this.#reconcileSent > 0) text = STAGE_CHECKING;
      this.stageTarget.textContent = text;
    }
  }

  // Arms the next verification POST (slot = #reconcileSent). No-op without a URL, after
  // 5 POSTs or once the backend says "exhausted".
  #scheduleReconcile() {
    if (!this.reconcileUrlValue || this.#reconcileDone) return;
    if (this.#reconcileSent >= RECONCILE_SLOTS_MS.length) return;
    const delay = Math.max(0, RECONCILE_SLOTS_MS[this.#reconcileSent] - (Date.now() - this.#startedAt));
    this.#reconcileTimer = setTimeout(() => this.#reconcile(), delay);
  }

  async #reconcile() {
    this.#reconcileTimer = null;
    if (!this.#abortController || this.#reconcileDone) return;
    this.#reconcileSent += 1;
    this.#renderProgress();
    const controller = this.#abortController;
    const res = await sendJSON("POST", this.reconcileUrlValue);
    // Stopped (terminal status / disconnect / retry) while the POST was in flight.
    if (this.#abortController !== controller) return;
    if (res.ok && res.data?.queued === false && res.data?.reason === "exhausted") {
      this.#reconcileDone = true;
      return;
    }
    this.#scheduleReconcile();
  }

  #stopPolling() {
    if (this.#progressTimer !== null) {
      clearInterval(this.#progressTimer);
      this.#progressTimer = null;
    }
    if (this.#reconcileTimer !== null) {
      clearTimeout(this.#reconcileTimer);
      this.#reconcileTimer = null;
    }
    if (this.#timer !== null) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  async #poll() {
    // Guard: controller already disconnected or polling stopped
    if (!this.#abortController) return;

    // Timeout reached — show the timeout panel and stop
    if (Date.now() - this.#startedAt >= this.timeoutValue) {
      this.#stopPolling();
      this.#renderProgress();
      this.#showPanel("timeout");
      return;
    }

    let res;
    try {
      res = await fetch(this.statusUrlValue, {
        signal: this.#abortController.signal,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
    } catch {
      // Network error or AbortError (from #stopPolling) — keep polling or already stopped
      return;
    }

    // 401/403/404: payment not found or not accessible — show failed, stop
    if (res.status === 401 || res.status === 403 || res.status === 404) {
      this.#stopPolling();
      this.#showPanel("failed");
      return;
    }

    // Other non-2xx server errors — keep polling
    if (!res.ok) return;

    let data;
    try {
      data = await res.json();
    } catch {
      return;
    }

    if (data.status === "failed") {
      this.#stopPolling();
      this.#showPanel("failed");
      return;
    }

    if (data.status === "paid") {
      this.#stopPolling();
      // refund_pending: money arrived but order cannot be fulfilled (late or unfulfilled
      // payment path).  Show the refundPending panel instead of success.
      if (data.refund_pending) {
        this.#showPanel("refundPending");
        return;
      }
      if (data.kind === "top_up") {
        // Polish decimal comma: "123.45" → "123,45"
        this.amountTarget.textContent = String(data.amount_pln ?? "").replace(".", ",");
        this.#showPanel("successTopUp");
      } else {
        this.orderNumberTarget.textContent = String(data.order_code ?? "");
        this.#showPanel("successOrder");
      }
      return;
    }

    // Pending with provider_state not_paid / paid_unconfirmed / unknown / null keeps the interval running.
  }

  // Exactly one panel visible at a time — set hidden on all then unhide the named one.
  #showPanel(name) {
    this.pendingTarget.hidden           = name !== "pending";
    this.successOrderTarget.hidden      = name !== "successOrder";
    this.successTopUpTarget.hidden      = name !== "successTopUp";
    this.failedTarget.hidden            = name !== "failed";
    this.notCompletedOrderTarget.hidden = name !== "notCompletedOrder";
    this.notCompletedTopUpTarget.hidden = name !== "notCompletedTopUp";
    this.timeoutTarget.hidden           = name !== "timeout";
    this.refundPendingTarget.hidden     = name !== "refundPending";
  }
}
